import { Button, ErrorContainer, Heading, IconPlus } from "@sentricmusic/ui";
import { useNavigate } from "@tanstack/react-router";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import {
  useGetMyAccount,
  useGetMyProfile,
  useGetMySongsDetailedPaginated,
} from "@/api/queries";
import useSiteConfigAuth from "@/api/sanity/queries/getSiteConfigWithCode";
import { hasAdminRights } from "@/auth/shared";
import useGetColours from "@/helpers/getColours";
import { sendProperty } from "@/helpers/helpers";
import useAppStore from "@/store/appStateZustand";
import { useUIStateStore } from "@/store/uiStateStore";

import SongsTable from "./SongsTable";
import SongSuggestions from "./SongSuggestions";

function Songs() {
  const { t } = useTranslation("songs");
  const selectedAccount = useAppStore(s => s.selectedAccount) as string;
  const navigate = useNavigate();
  const { data: siteConfig } = useSiteConfigAuth();
  const isTunecore = useUIStateStore(state => state.isTunecore);
  const isRoughTrade = siteConfig?.display_name?.includes("Rough Trade Publishing") ?? false;

  useEffect(() => {
    if (isRoughTrade) {
      void navigate({ to: "/statements" });
    }
  }, [isRoughTrade, navigate]);

  const flags = useFlags();
  const newAddSongs = Boolean(flags["pd-add-songs-rework"]);

  const profile = useGetMyProfile();

  const myAccount = useGetMyAccount({
    artistaccountcode: selectedAccount,
  });
  const { highlightColour } = useGetColours();

  const tableState = useAppStore(s => s.songsTable);
  const updateSongsTable = useAppStore(s => s.updateSongsTable);

  const songs = useGetMySongsDetailedPaginated({
    aac: selectedAccount,
    wc: tableState.writerCode,
    perPage: tableState.perPage,
    page: tableState.page,
    sortBy: tableState.sortBy?.[0]?.id,
    desc: tableState.sortBy?.[0]?.desc,
    globalFilter: tableState.globalFilter,
  });

  const adminRights = hasAdminRights(myAccount?.data);

  useEffect(() => {
    if (selectedAccount) {
      // Reset the filters and pagination when switching accounts
      updateSongsTable({ globalFilter: null, writerCode: null, page: 1 });
    }
  }, [selectedAccount, updateSongsTable]);

  useEffect(() => {
    if (songs.isSuccess && songs.data?.works !== null) {
      sendProperty({
        property: "songs",
        value: songs.data?.works.length,
        email: profile?.data?.email,
      });
    }
  }, [songs, profile]);

  return (
    <div className="flex flex-wrap w-full">
      <div className="w-full lg:min-h-screen-minus-header">
        <div className={newAddSongs ? "" : "mx-4 my-12 lg:mx-12 xl:mx-20 2xl:mx-40"}>
          <div className="flex flex-wrap items-baseline justify-between mb-4">
            <Heading as="h3">{t("songs:songs-header")}</Heading>
            <Button
              variant="primary"
              icon={<IconPlus size="md" className="stroke-ink-primary" />}
              id="btnSongsAddNew"
              disabled={!adminRights}
              onClick={() => {
                if (adminRights) {
                  void navigate({ to: "/songs/create" });
                }
              }}
              label={t("songs:add-new-song-button")}
            />
          </div>

          {!siteConfig?.disable_suggestions && Boolean(flags["pd-spotify-suggestions-feature"]) && (
            <div className={`${isTunecore ? "bg-layer-inverse" : "bg-white"} rounded-lg p-4 lg:p-8 mb-6`}>
              <Heading as="h4">
                {isTunecore ? t("songs:suggestions-header") : t("songs:spotify-header")}
              </Heading>
              <SongSuggestions highlightColour={highlightColour} />
            </div>
          )}

          <div
            id="songsTable"
            className={`${isTunecore ? "bg-layer-inverse" : "bg-white"} rounded-lg p-4 lg:p-8`}
          >
            <Heading as="h4" className="mb-4">{t("songs:song-library-header")}</Heading>
            {songs.isError
              ? (
                  <ErrorContainer message={String(t("songs:error-loading-songs", "Failed to load songs. Please refresh and try again."))} />
                )
              : (
                  <SongsTable
                    highlightColour={highlightColour}
                    data={songs.data?.works ?? []}
                    total={songs.data?.total ?? 0}
                    isLoading={songs.isLoading}
                    isFetching={songs.isFetching}
                    adminRights={adminRights}
                  />
                )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Songs;
